import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Alert, Card, Form, Select, Skeleton, Space, Typography } from "antd";
import { Link } from "react-router-dom";

import {
  currentOfferVariantsFilter,
  resolveCurrentOffer,
  type CurrentOfferVariant,
} from "@fitness-app/data-models/domain/currentOffer/resolveCurrentOffer";

import { supabase } from "~/store/initializeStore";
import { CurrentOfferStatus } from "./CurrentOfferStatus";
import { currentOfferQueryKey, useCurrentOfferConfig } from "./useCurrentOfferConfig";

/** Strona konfiguracji: właściciel raz wskazuje produkt (Hollywood), na którego koszykach wybiera się promocje. */
export const CurrentOfferCard = (): React.ReactElement => {
  const { configQuery, saveMutation } = useCurrentOfferConfig();
  const config = configQuery.data?.data ?? null;
  const productId = config?.productId?.trim();
  const [now] = useState(() => new Date());

  const productsQuery = useQuery({
    queryKey: [...currentOfferQueryKey, "products"],
    retry: false,
    queryFn: async () => {
      const { data, error } = await supabase.from("product").select("id, name").order("name", { ascending: true });

      if (error) throw error;

      return data ?? [];
    },
  });

  const variantsQuery = useQuery({
    queryKey: [...currentOfferQueryKey, "variants", productId, config?.variantId, config?.renewalVariantId],
    enabled: !!productId,
    retry: false,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("checkout_variant")
        .select("id, slug, status, target, excluded, default, activeUntil, createdAt, metadata")
        .eq("productId", productId!)
        .or(currentOfferVariantsFilter(config));

      if (error) throw error;

      return (data ?? []) as CurrentOfferVariant[];
    },
  });

  return (
    <Card size="small" title="Aktualna oferta (stałe linki z maili)" style={{ marginBottom: 16 }}>
      {configQuery.isLoading ? (
        <Skeleton active paragraph={{ rows: 1 }} />
      ) : configQuery.isError || !configQuery.data ? (
        <Alert
          type="warning"
          showIcon
          message="Brak konfiguracji aktualnej oferty"
          description="Nie znaleziono rekordu app_config typu current_offer. Oba linki prowadzą na /pakiety."
        />
      ) : (
        <Space direction="vertical" size="middle" className="w-full">
          <Form layout="vertical">
            <Form.Item
              label="Produkt"
              tooltip="Na liście koszyków tego produktu pojawi się kolumna „W mailach”, w której Sales Manager wybiera promocje dla linków z maili ActiveCampaign."
              style={{ marginBottom: 0 }}
            >
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="Wybierz produkt Hollywood"
                loading={productsQuery.isLoading || saveMutation.isLoading}
                value={productId || undefined}
                onChange={(value?: string) =>
                  saveMutation.mutate({ productId: value ?? "", variantId: "", renewalVariantId: "" })
                }
                options={(productsQuery.data ?? []).map((product) => ({ value: product.id, label: product.name }))}
                style={{ maxWidth: 420 }}
              />
            </Form.Item>
          </Form>

          {productId && variantsQuery.isLoading ? (
            <Skeleton active paragraph={{ rows: 2 }} />
          ) : (
            (["new", "renewal"] as const).map((audience) => (
              <CurrentOfferStatus
                key={audience}
                audience={audience}
                resolution={resolveCurrentOffer(config, variantsQuery.data ?? [], audience, now)}
              />
            ))
          )}

          {productId && (
            <Typography.Text type="secondary">
              Promocje wybiera się na <Link to={`/products/${productId}/sales`}>liście koszyków produktu</Link>{" "}
              (zakładka „Warianty koszyka”).
            </Typography.Text>
          )}
        </Space>
      )}
    </Card>
  );
};
